import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Button, Tab, Typography } from '@dreamteam-monorepo/ui';
import { JoinLeaguePageHero } from '@/components/JoinLeaguePageHero/JoinLeaguePageHero';
import { JoinLeagueCard } from '@/components/JoinLeagueCard/JoinLeagueCard';
import { useLeaguesStore } from '@/stores/LeaguesStore';
import { useAuthStore } from '@/stores/AuthStore';
import { BELeague } from '@/lib/leagueMappers';
import { getErrorMessage } from '@/lib/network';
import { showGhLogin } from '@/lib/ghAuth';
import { ContentAdSlot } from '@/ads';
import { trackFantasyEvent } from '@/analytics';
import { useJoinableLeagues } from '@/hooks/useJoinableLeagues';

type LeagueFilter = 'all' | 'celebrity' | 'public';

const EMPTY_MESSAGES: Record<LeagueFilter, string> = {
  all: 'No public leagues available.',
  celebrity: 'No celebrity leagues available.',
  public: 'No public leagues available.',
};

const JoinLeague: FC = () => {
  const navigate = useNavigate();
  const userId = useAuthStore(s => s.userId);
  const { getLeagueByCode, joinLeagueByCode, joining } = useLeaguesStore();

  const [pin, setPin] = useState('');
  const [findLoading, setFindLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [foundLeague, setFoundLeague] = useState<BELeague | null>(null);
  const [joiningId, setJoiningId] = useState<string | null>(null);
  const [filter, setFilter] = useState<LeagueFilter>('all');
  const celebrityFilter = filter === 'all' ? undefined : filter === 'celebrity';
  const {
    data: leaguePages,
    isLoading: loadingLeagues,
    isError: leaguesError,
    refetch: refetchLeagues,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useJoinableLeagues(celebrityFilter);

  const joinableLeagues = useMemo(
    () => leaguePages?.pages.flatMap(page => page.data.items) ?? [],
    [leaguePages]
  );
  const availableLeagueCount = leaguePages?.pages[0]?.data.pagination.total ?? 0;
  const hasInitialLeaguesError = leaguesError && joinableLeagues.length === 0;

  const handlePinChange = (value: string) => {
    setPin(value);
    if (notFound) setNotFound(false);
    if (foundLeague) setFoundLeague(null);
  };

  const handleFindLeague = async () => {
    const code = pin.trim();
    if (!code) return;
    setFindLoading(true);
    setNotFound(false);
    setFoundLeague(null);
    try {
      const league = await getLeagueByCode(code);
      setFoundLeague(league);
    } catch {
      setNotFound(true);
    } finally {
      setFindLoading(false);
    }
  };

  const handleJoin = async (league: BELeague) => {
    if (!userId) {
      toast.error('You must be logged in to join a league');
      showGhLogin();
      return;
    }
    setJoiningId(league.id);
    try {
      const leagueId = await joinLeagueByCode(league.join_code, userId);
      trackFantasyEvent('leagueJoinedByCode');
      navigate(`/leagues/${leagueId}`, { state: { justJoined: true } });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not join the league. Please try again.'));
    } finally {
      setJoiningId(null);
    }
  };

  return (
    <div className="components light flex flex-col">
      <JoinLeaguePageHero
        pin={pin}
        onPinChange={handlePinChange}
        onFindLeague={handleFindLeague}
        onBack={() => {
          trackFantasyEvent('leagueLink', { link_name: 'Back to Leagues' });
          navigate('/leagues');
        }}
        onCreateLeague={() => {
          trackFantasyEvent('leagueLink', { link_name: 'Create League' });
          navigate('/leagues/create');
        }}
        findLoading={findLoading}
        notFound={notFound}
        foundLeague={foundLeague}
        onJoinFoundLeague={foundLeague ? () => handleJoin(foundLeague) : undefined}
        joiningFound={joining && joiningId === foundLeague?.id}
      />

      <div className="bg-white w-full">
        <div className="max-w-[846px] mx-auto w-full px-[--padding-md] sm:px-0 py-[--padding-xl]">
          <div className="flex flex-col gap-[--padding-lg] sm:flex-row sm:items-center sm:gap-[--padding-xl]">
            <div className="flex gap-[--padding-xs]">
              <Tab selected={filter === 'all'} onClick={() => setFilter('all')} className="flex-1">
                ALL
              </Tab>
              <Tab
                selected={filter === 'celebrity'}
                onClick={() => setFilter(filter === 'celebrity' ? 'all' : 'celebrity')}
                className="flex-1"
              >
                CELEBRITY
              </Tab>
              <Tab
                selected={filter === 'public'}
                onClick={() => setFilter(filter === 'public' ? 'all' : 'public')}
                className="flex-1"
              >
                PUBLIC
              </Tab>
            </div>
            {!loadingLeagues && !hasInitialLeaguesError && (
              <Typography
                variant="subheadline020"
                className="text-[--color-neutral-900] sm:shrink-0"
              >
                {availableLeagueCount} leagues available
              </Typography>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-[846px] mx-auto w-full px-[--padding-md] sm:px-0 pb-[--padding-3xl]">
        {loadingLeagues ? (
          <div className="flex flex-col gap-[--padding-sm]">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-[72px] bg-white rounded-[--corner-md] border border-[#eae9fd] animate-pulse"
              />
            ))}
          </div>
        ) : hasInitialLeaguesError ? (
          <div className="flex flex-col items-start gap-[--padding-sm]">
            <Typography variant="paragraph020" className="text-[--color-neutral-600]">
              We couldn't load the available leagues.
            </Typography>
            <Button color="blue" variant="outlined" onClick={() => refetchLeagues()}>
              Try Again
            </Button>
          </div>
        ) : joinableLeagues.length === 0 ? (
          <Typography variant="paragraph020" className="text-[--color-neutral-600]">
            {EMPTY_MESSAGES[filter]}
          </Typography>
        ) : (
          <div className="flex flex-col gap-[--padding-sm]">
            {joinableLeagues.map(league => (
              <JoinLeagueCard
                key={league.id}
                league={league}
                onJoin={() => handleJoin(league)}
                joining={joining && joiningId === league.id}
              />
            ))}
            {!isFetchNextPageError && (hasNextPage || isFetchingNextPage) && (
              <div className="flex justify-center pt-[--padding-md]">
                <Button
                  color="blue"
                  variant="outlined"
                  onClick={() => fetchNextPage()}
                  disabled={!hasNextPage}
                  loading={isFetchingNextPage}
                >
                  Load More
                </Button>
              </div>
            )}
            {isFetchNextPageError && (
              <div className="flex flex-col items-center gap-[--padding-xs] pt-[--padding-sm]">
                <Typography variant="paragraph020" className="text-[--color-neutral-600]">
                  We couldn't load more leagues.
                </Typography>
                <Button color="blue" variant="text" onClick={() => fetchNextPage()}>
                  Try Again
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      <ContentAdSlot id="ad-boa-leaderboard" />
    </div>
  );
};

export default JoinLeague;
